// src/pages/SimpleUploadPage.js
import React, { useState } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const SimpleUploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [downloadURL, setDownloadURL] = useState('');
  const { currentUser } = useAuth();

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (!currentUser && !window.confirm('No user detected. Use jane.doe@cooc.com.au for testing?')) {
      setError('Upload canceled - no user logged in');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError('');
    setSuccess('');
    setDownloadURL('');

    try {
      // Create a reference to the file in storage
      const storage = getStorage();
      const userEmail = currentUser?.email || 'jane.doe@cooc.com.au'; // Fallback for testing
      
      // Use a simpler path first for troubleshooting
      const filePath = `uploads/${Date.now()}_${file.name}`;
      console.log(`Attempting to upload to path: ${filePath}`);
      const fileRef = ref(storage, filePath);

      console.log('Storage reference created, starting upload...');
      // Upload the file
      const uploadTask = uploadBytesResumable(fileRef, file);

      // Track progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
          console.log(`Upload progress: ${progress}%`);
        },
        (error) => {
          console.error('Upload error:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Error details:', error.serverResponse || 'No server response');
          
          let errorMessage = `Upload failed: ${error.message}`;
          
          // Enhanced error handling with suggestions
          if (error.code === 'storage/unauthorized') {
            errorMessage = 'Access denied. Firebase Storage rules may be preventing this upload.';
          } else if (error.code === 'storage/canceled') {
            errorMessage = 'Upload was canceled.';
          } else if (error.code === 'storage/unknown') {
            errorMessage = 'Unknown error occurred. This could be due to network issues or Firebase configuration.';
          }
          
          setError(errorMessage);
          setUploading(false);
        },
        async () => {
          // Upload completed successfully
          console.log('Upload completed');
          
          try {
            // Get download URL
            const url = await getDownloadURL(fileRef);
            setDownloadURL(url);
            console.log('File URL:', url);
            
            // Save metadata to Firestore
            const db = getFirestore();
            console.log(`Saving to Firestore: Users/${userEmail}/Files`);
            try {
              const fileDoc = await addDoc(
                collection(db, 'Users', userEmail, 'Files'),
                {
                  fileName: file.name,
                  contentType: file.type,
                  fileSize: file.size,
                  path: filePath,
                  downloadURL: url,
                  uploadedAt: serverTimestamp(),
                  status: 'completed',
                  category: 'Uncategorized',
                  subCategory: 'Uncategorized'
                }
              );
              
              console.log('Document added with ID:', fileDoc.id);
              setSuccess(`File uploaded successfully: ${file.name}`);
            } catch (firestoreError) {
              console.error('Firestore error:', firestoreError);
              // We still succeeded with the upload, so mark as partial success
              setSuccess(`File uploaded successfully, but could not save metadata: ${file.name}`);
            }
          } catch (error) {
            console.error('Post-upload error:', error);
            setError(`File uploaded but metadata could not be saved: ${error.message}`);
          }
          
          setUploading(false);
        }
      );
    } catch (error) {
      console.error('Setup error:', error);
      setError(`Upload setup failed: ${error.message}`);
      setUploading(false);
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '600px', 
      margin: '0 auto', 
      fontFamily: 'Arial, sans-serif' 
    }}>
      <h1 style={{ marginBottom: '20px' }}>Simple Upload Test</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <input 
          type="file" 
          onChange={handleFileChange} 
          disabled={uploading}
          style={{ marginBottom: '10px', display: 'block' }}
        />
        
        <button 
          onClick={handleUpload} 
          disabled={!file || uploading}
          style={{
            backgroundColor: '#128C7E',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: !file || uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </button>
      </div>
      
      {uploading && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${progress}%`, 
              backgroundColor: '#128C7E', 
              height: '10px',
              transition: 'width 0.2s'
            }}></div>
          </div>
          <p>{Math.round(progress)}% Uploaded</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {success && (
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          color: '#2e7d32', 
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Success:</strong> {success}
        </div>
      )}
      
      {downloadURL && (
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <p style={{ marginBottom: '10px' }}>File uploaded successfully:</p>
          <a 
            href={downloadURL} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#128C7E',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            View Uploaded File
          </a>
        </div>
      )}
      
      <div style={{
        backgroundColor: '#eceff1',
        padding: '15px',
        borderRadius: '4px',
        marginTop: '30px'
      }}>
        <h3 style={{ marginTop: '0' }}>Debug Information</h3>
        <p><strong>User:</strong> {currentUser ? currentUser.email : 'Not logged in'}</p>
        <p><strong>File:</strong> {file ? `${file.name} (${file.type}, ${file.size} bytes)` : 'None selected'}</p>
      </div>
    </div>
  );
};

export default SimpleUploadPage;